/**
 * AccountRow — one row inside any account list (Overview groups + the
 * per-category tabs). Renders the avatar, name, institution/type meta,
 * category-specific extras (credit-card utilization bar, investment day
 * change), balance, last-updated line, and a trailing chevron.
 *
 * Phase 84.22.4 (multi-currency correctness):
 *   • The balance is shown in the account's NATIVE currency
 *     (`account.currency`) so a Dubai account never says AED for money
 *     that actually lives in INR.
 *   • When the account currency differs from the user's base currency
 *     AND we have an FX rate available, a small "≈ AED 12,400" hint
 *     appears under the native balance so the user can still read the
 *     value in their headline currency.
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import AccountAvatar from './AccountAvatar';
import FreshnessChip from './FreshnessChip';
import { ACCOUNT_TYPE_LABEL, ACCOUNTS_TONE } from '@/lib/accountsDemo';
import { formatMoney } from '@/lib/format';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function AccountRow({ account, onClick, baseCurrency = 'INR', resource = null, excluded = null }) {
  const abs = Math.abs(account.balance || 0);
  const isLiability  = account.category === 'liabilities';
  const isInvestment = account.category === 'investments';
  const nativeCurrency = (account.currency || baseCurrency || 'INR').toUpperCase();
  const util = account.credit_limit ? Math.min(100, (abs / account.credit_limit) * 100) : null;

  // Phase 84.22.10 (Wave B3) — read FX helpers straight from the
  // app-root CurrencyContext. Previously these came in as props from
  // Accounts.js; the prop-drill is gone.
  const ctx = useCurrencyContext();
  const rateAvailable = nativeCurrency in (ctx.ratesToBase || {});
  const showBaseHint = rateAvailable && nativeCurrency !== baseCurrency;
  const baseAbs = showBaseHint ? ctx.toBase(abs, nativeCurrency) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-offwhite/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors group text-left"
      data-testid={`accounts-row-${account.id}`}
    >
      <AccountAvatar account={account} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-charcoal leading-none mb-1 truncate">{account.name}</p>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground truncate">
          {account.institution && (
            <>
              <span className="truncate">{account.institution}</span>
              <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground/40 shrink-0" />
            </>
          )}
          <span className="truncate">{account.subtype_label || ACCOUNT_TYPE_LABEL[account.type] || account.type}</span>
          {account.account_number && (
            <>
              <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground/40 shrink-0" />
              <span className="num truncate opacity-70">{account.account_number}</span>
            </>
          )}
        </div>
      </div>

      {/* Credit card utilization (kept in native currency — this is a
          single-account ratio, cross-currency math doesn't apply). */}
      {util !== null && (
        <div className="hidden sm:block text-right w-32 shrink-0">
          <p className="text-[11px] text-muted-foreground mb-1">
            {formatMoney(abs, nativeCurrency)} of {formatMoney(account.credit_limit, nativeCurrency)}
          </p>
          <div className="h-1 bg-border/60 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${util}%`,
                backgroundColor: util > 80 ? ACCOUNTS_TONE.warning : ACCOUNTS_TONE.positive,
              }}
            />
          </div>
        </div>
      )}

      {/* Day change for investments — native currency of the holding. */}
      {isInvestment && account.day_change != null && (
        <div className="hidden sm:block text-right shrink-0">
          <p
            className="text-[11px] font-medium num"
            style={{ color: account.day_change >= 0 ? ACCOUNTS_TONE.positive : ACCOUNTS_TONE.liability }}
          >
            {account.day_change >= 0 ? '+' : '−'}{formatMoney(Math.abs(account.day_change), nativeCurrency)} today
          </p>
        </div>
      )}

      {/* Balance — native currency, with an optional "≈ <base>" hint
          underneath when native != base and an FX rate is available. */}
      <div className="text-right shrink-0">
        <p
          className="num text-sm font-semibold"
          data-testid={`accounts-row-balance-${account.id}`}
          style={{ color: isLiability ? ACCOUNTS_TONE.liability : '#1E1E1E' }}
        >
          {isLiability ? '−' : ''}{formatMoney(abs, nativeCurrency)}
        </p>
        {showBaseHint && (
          <p
            className="text-[11px] text-muted-foreground/80 mt-0.5 num"
            data-testid={`accounts-row-balance-base-${account.id}`}
          >
            ≈ {isLiability ? '−' : ''}{formatMoney(baseAbs, baseCurrency)}
          </p>
        )}
        {/* Phase 86 — the canonical resolver decides what a balance is worth and
            how much it can be trusted. When it deliberately left this resource
            out of the totals we say so on the row rather than letting the money
            silently disappear from a subtotal. */}
        {excluded ? (
          <p className="text-[11px] text-muted-foreground mt-1" data-testid={`accounts-row-excluded-${account.id}`}>
            Not counted · {excluded.reason_label}
          </p>
        ) : (
          <>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {resource?.as_of
                ? `Updated ${fmtDate(resource.as_of)}`
                : account.last_updated ? `Updated ${fmtDate(account.last_updated)}` : 'Not confirmed yet'}
            </p>
            {resource?.freshness && (
              <div className="flex justify-end mt-1">
                <FreshnessChip freshness={resource.freshness} />
              </div>
            )}
          </>
        )}
      </div>

      <span className="text-muted-foreground/30 group-hover:text-muted-foreground transition-colors shrink-0" aria-hidden>
        <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}
